import { useState, useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Monitor, ArrowLeft, User, Mail, Lock, Building, Eye, EyeOff, CheckCircle2, Loader2, Phone } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";

import { UpdatePasswordForm } from "@/components/auth/UpdatePasswordForm";

const Auth = () => {
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    phone: ""
  });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Estados para recuperação de senha
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Estado local para forçar a renderização da recuperação se o useAuth falhar
  const [forceRecoveryMode, setForceRecoveryMode] = useState(false);

  const navigate = useNavigate();
  const { signIn, signUp, user, loading, resetPassword, isPasswordRecovery } = useAuth();

  // Detectar flag do interceptor global (index.html) e restaurar sessão se necessário
  useEffect(() => {
    const restoreSessionFromUrl = async () => {
      try {
        const storedRecovery = localStorage.getItem('__recovery_flow');
        if (storedRecovery) {
          const data = JSON.parse(storedRecovery);

          // Se tiver passado menos de 60 segundos
          if (Date.now() - data.timestamp < 60000) {
            console.log('🔒 Auth [STORAGE] - Flag de recuperação detectada!', data);

            // Só ativamos o modo de recuperação SE conseguirmos restaurar a sessão ou se já tivermos sessão
            let sessionRestored = false;

            // Extrair access_token e refresh_token da URL salva
            let accessToken = null;
            let refreshToken = null;

            if (data.url) {
              const savedUrl = new URL(data.url);
              // Tentar do Hash
              if (savedUrl.hash) {
                const hashParams = new URLSearchParams(savedUrl.hash.substring(1));
                accessToken = hashParams.get('access_token');
                refreshToken = hashParams.get('refresh_token');
              }
              // Tentar da Query
              if (!accessToken) {
                accessToken = savedUrl.searchParams.get('access_token');
                refreshToken = savedUrl.searchParams.get('refresh_token');
              }
            }

            if (accessToken && refreshToken) {
              console.log('🔒 Auth [RECOVERY] - Tokens encontrados! Definindo sessão...');
              const { error } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });

              if (error) {
                console.error('❌ Auth [RECOVERY] - Falha ao definir sessão:', error);
              } else {
                console.log('✅ Auth [RECOVERY] - Sessão restaurada com sucesso!');
                sessionRestored = true;
              }
            } else {
              // Se não achou token na URL salva, verifica se o usuário já está logado (pode ter logado pelo magic link automático)
              const { data: currentSession } = await supabase.auth.getSession();
              if (currentSession.session) {
                console.log('✅ Auth [RECOVERY] - Sessão já existente detectada.');
                sessionRestored = true;
              } else {
                console.warn('⚠️ Auth [RECOVERY] - Tokens não encontrados e sem sessão ativa.');
              }
            }

            if (sessionRestored) {
              setForceRecoveryMode(true);
            }

            // Limpar a flag APÓS o processamento
            localStorage.removeItem('__recovery_flow');
          }
        }
      } catch (e) {
        console.error('Erro ao ler flag de recuperação:', e);
      }
    };

    restoreSessionFromUrl();
  }, []);

  // useLayoutEffect roda ANTES da pintura da tela, garantindo que pegamos o hash antes dele sumir
  useLayoutEffect(() => {
    // 1. Verificação DO HASH ATUAL (Implicit Flow)
    const hash = window.location.hash;

    // 2. Verificação dos QUERY PARAMS (PKCE Flow - Novo Padrão Supabase)
    const search = window.location.search;
    const searchParams = new URLSearchParams(search);
    const typeFromQuery = searchParams.get('type');
    const codeFromQuery = searchParams.get('code');

    console.log('🔒 Auth [LAYOUT] - Hash Bruto:', hash);
    console.log('🔒 Auth [LAYOUT] - Query Bruta:', search);

    // Detecção Hibrida: Hash ou Query
    const isRecoveryHash = hash && hash.includes('type=recovery');
    const isRecoveryQuery = typeFromQuery === 'recovery';

    if (isRecoveryHash || isRecoveryQuery) {
      console.log('🔒 Auth [LAYOUT] - RECOVERY DETECTADO NA URL (Hash ou Query)! ATIVANDO MODO FORÇADO.');
      setForceRecoveryMode(true);
    }

    // 2. Listener do Supabase (Backup)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log(`🔒 Auth [EVENT] - ${event}`);
      if (event === 'PASSWORD_RECOVERY') {
        console.log('🔒 Auth [EVENT] - PASSWORD_RECOVERY Disparado!');
        setForceRecoveryMode(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    }
  }, []);

  // Combinar a detecção do hook com a detecção local
  const showRecoveryUI = isPasswordRecovery || forceRecoveryMode;

  // Redirecionar se já estiver autenticado (EXCETO se for recuperação de senha)
  useEffect(() => {
    console.log('🔒 Auth - Check Redirect:', { userId: user?.id, loading, showRecoveryUI, forceRecoveryMode });

    // CRÍTICO: Se houver QUALQUER indício de recuperação, CANCELAR o redirect
    if (showRecoveryUI || window.location.hash.includes('type=recovery')) {
      console.log('🔒 Auth - REDIRECT CANCELADO: Modo recuperação detectado.');
      return;
    }

    if (user && !loading) {
      console.log('🔒 Auth - Usuário autenticado normal, redirecionando...');

      // Verificar se o email termina com @letswatch.com.br
      const isLetswatchDomain = user.email?.endsWith('@letswatch.com.br');

      if (isLetswatchDomain) {
        console.log('🔒 Auth - Email @letswatch.com.br detectado, redirecionando para Painel Geral');
        navigate('/paneladmin');
      } else {
        console.log('🔒 Auth - Redirecionando para /dashboard');
        navigate('/dashboard');
      }
    }
  }, [user, loading, navigate, showRecoveryUI, forceRecoveryMode]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) return;

    console.log('🔒 Auth - Tentando login com:', loginData.email);
    setIsLoading(true);
    const { error, data } = await signIn(loginData.email, loginData.password);

    if (!error) {
      console.log('🔒 Auth - Login bem-sucedido, redirecionando...');

      // =========================================================================
      // LIMPEZA DO CACHE DE MÍDIAS AO TROCAR DE USUÁRIO
      // Garante que o novo usuário não veja mídias do usuário anterior
      // =========================================================================
      const newUserId = data?.user?.id;
      const cachedUserId = localStorage.getItem('player_cache_user_id');

      if (newUserId && cachedUserId && newUserId !== cachedUserId) {
        console.log('🧹 [AUTH] Usuário diferente detectado, limpando cache de mídias...');
        console.log('🧹 [AUTH] Usuário anterior:', cachedUserId);
        console.log('🧹 [AUTH] Novo usuário:', newUserId);

        // Limpar IndexedDB (cache de mídias)
        if ('indexedDB' in window) {
          try {
            indexedDB.deleteDatabase('watch-media-cache-v2');
            console.log('✅ [AUTH] Cache IndexedDB limpo com sucesso');
          } catch (e) {
            console.error('❌ [AUTH] Erro ao limpar cache IndexedDB:', e);
          }
        }

        // Limpar sessionStorage relacionado ao player
        sessionStorage.removeItem('player_activated_user_id');
        sessionStorage.removeItem('player_device_code');
        sessionStorage.removeItem('player_activated_by_companion');
      }

      // Salvar o novo userId do cache
      if (newUserId) {
        localStorage.setItem('player_cache_user_id', newUserId);
      }

      // Verificar se o email termina com @letswatch.com.br
      const isLetswatchDomain = loginData.email.endsWith('@letswatch.com.br');

      if (isLetswatchDomain) {
        console.log('🔒 Auth - Email @letswatch.com.br detectado, redirecionando para Painel Geral');
        navigate('/paneladmin');
      } else {
        console.log('🔒 Auth - Redirecionando para /dashboard');
        navigate('/dashboard');
      }
    } else {
      console.error('🔒 Auth - Erro no login:', error);
    }
    setIsLoading(false);
  };

  // Limpar as funções antigas handleUpdatePassword que não são mais usadas aqui

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (signupData.password !== signupData.confirmPassword) {
      return;
    }

    if (!signupData.email || !signupData.password) return;

    setIsLoading(true);
    const { error } = await signUp(signupData.email, signupData.password, undefined, signupData.companyName, signupData.phone);

    if (!error) {
      // Usuário será redirecionado automaticamente após confirmação do email
      setSignupData({ email: "", password: "", confirmPassword: "", companyName: "", phone: "" });
    }
    setIsLoading(false);
  };

  // Handler para recuperação de senha
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;

    setIsResetting(true);
    const { error } = await resetPassword(resetEmail);

    if (!error) {
      setResetSuccess(true);
    }
    setIsResetting(false);
  };

  // Handler para fechar o modal de reset
  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setResetEmail("");
    setResetSuccess(false);
  };

  // Handler para abrir modal de reset com email preenchido
  const handleOpenResetModal = () => {
    setResetEmail(loginData.email);
    setShowResetModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-950 via-green-900 to-emerald-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Efeitos de raios neon/laser animados */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Raios brancos da esquerda para direita */}
        <div className="absolute top-[12%] left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-white to-transparent opacity-70 animate-[slideRight_4s_linear_infinite]" style={{ boxShadow: '0 0 20px 4px rgba(255, 255, 255, 0.7)' }}></div>
        <div className="absolute top-[42%] left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-[slideRight_5.5s_linear_infinite_1.5s]" style={{ boxShadow: '0 0 15px 3px rgba(255, 255, 255, 0.6)' }}></div>
        <div className="absolute top-[78%] left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-white to-transparent opacity-60 animate-[slideRight_6s_linear_infinite_3s]" style={{ boxShadow: '0 0 18px 3px rgba(255, 255, 255, 0.65)' }}></div>

        {/* Raios verdes claros da esquerda para direita */}
        <div className="absolute top-[28%] left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-lime-300 to-transparent opacity-60 animate-[slideRight_5s_linear_infinite_0.8s]" style={{ boxShadow: '0 0 18px 3px rgba(190, 242, 100, 0.7)' }}></div>
        <div className="absolute top-[68%] left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-green-300 to-transparent opacity-55 animate-[slideRight_4.5s_linear_infinite_2.5s]" style={{ boxShadow: '0 0 22px 4px rgba(134, 239, 172, 0.7)' }}></div>

        {/* Raios brancos da direita para esquerda */}
        <div className="absolute top-[22%] right-0 w-full h-[1px] bg-gradient-to-l from-transparent via-white to-transparent opacity-55 animate-[slideLeft_5s_linear_infinite_0.5s]" style={{ boxShadow: '0 0 16px 3px rgba(255, 255, 255, 0.6)' }}></div>
        <div className="absolute top-[58%] right-0 w-full h-[2px] bg-gradient-to-l from-transparent via-white to-transparent opacity-65 animate-[slideLeft_4.5s_linear_infinite_2s]" style={{ boxShadow: '0 0 20px 4px rgba(255, 255, 255, 0.7)' }}></div>
        <div className="absolute top-[88%] right-0 w-full h-[1px] bg-gradient-to-l from-transparent via-white to-transparent opacity-45 animate-[slideLeft_6s_linear_infinite_3.5s]" style={{ boxShadow: '0 0 14px 2px rgba(255, 255, 255, 0.55)' }}></div>

        {/* Raios verdes claros da direita para esquerda */}
        <div className="absolute top-[35%] right-0 w-full h-[2px] bg-gradient-to-l from-transparent via-emerald-300 to-transparent opacity-60 animate-[slideLeft_5.5s_linear_infinite_1s]" style={{ boxShadow: '0 0 20px 4px rgba(110, 231, 183, 0.7)' }}></div>
        <div className="absolute top-[72%] right-0 w-full h-[1px] bg-gradient-to-l from-transparent via-teal-200 to-transparent opacity-50 animate-[slideLeft_4s_linear_infinite_2.8s]" style={{ boxShadow: '0 0 16px 3px rgba(153, 246, 228, 0.65)' }}></div>

        {/* Raios diagonais sutis */}
        <div className="absolute top-[48%] left-0 w-[150%] h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent opacity-35 animate-[slideRight_8s_linear_infinite] rotate-[12deg] origin-left"></div>
        <div className="absolute top-[62%] right-0 w-[150%] h-[1px] bg-gradient-to-l from-transparent via-lime-200/40 to-transparent opacity-30 animate-[slideLeft_7s_linear_infinite_2s] -rotate-[8deg] origin-right"></div>

        {/* Partículas brilhantes */}
        <div className="absolute top-[18%] left-[12%] w-2 h-2 rounded-full bg-white opacity-70 animate-pulse" style={{ boxShadow: '0 0 12px 4px rgba(255, 255, 255, 0.9)' }}></div>
        <div className="absolute top-[52%] right-[18%] w-1.5 h-1.5 rounded-full bg-lime-200 opacity-60 animate-pulse" style={{ animationDelay: '1s', boxShadow: '0 0 10px 3px rgba(217, 249, 157, 0.8)' }}></div>
        <div className="absolute top-[82%] left-[22%] w-1 h-1 rounded-full bg-white opacity-50 animate-pulse" style={{ animationDelay: '2s', boxShadow: '0 0 8px 3px rgba(255, 255, 255, 0.7)' }}></div>
        <div className="absolute top-[38%] right-[28%] w-1.5 h-1.5 rounded-full bg-green-200 opacity-55 animate-pulse" style={{ animationDelay: '0.5s', boxShadow: '0 0 10px 3px rgba(187, 247, 208, 0.75)' }}></div>
        <div className="absolute top-[65%] left-[8%] w-1 h-1 rounded-full bg-emerald-200 opacity-45 animate-pulse" style={{ animationDelay: '1.5s', boxShadow: '0 0 8px 2px rgba(167, 243, 208, 0.7)' }}></div>
      </div>

      {/* Estilos de animação inline */}
      <style>{`
        @keyframes slideRight {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes slideLeft {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>

      <div className="w-full max-w-md relative z-10">


        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-2">
              <div className="relative w-[340px] h-28 flex items-center justify-center">
                <img
                  src="https://link.letswatch.com.br/storage/27001/new-watch-verde.webp"
                  alt="Watch Logo"
                  className="w-auto h-28 object-contain drop-shadow-lg scale-110"
                />
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {showRecoveryUI ? (
              <UpdatePasswordForm />
            ) : (
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login">Entrar</TabsTrigger>
                  <TabsTrigger value="signup">Cadastrar</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="login-email"
                          type="email"
                          value={loginData.email}
                          onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="seu@email.com"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="login-password">Senha</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="login-password"
                          type={showLoginPassword ? "text" : "password"}
                          value={loginData.password}
                          onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Digite sua senha"
                          className="pl-10 pr-10"
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1 h-8 w-8 hover:bg-transparent"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                        >
                          {showLoginPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-11 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                      disabled={isLoading}
                    >
                      {isLoading ? "Entrando..." : "Entrar"}
                    </Button>

                    {/* Link de recuperação de senha */}
                    <div className="text-center pt-2">
                      <button
                        type="button"
                        onClick={handleOpenResetModal}
                        className="text-sm text-gray-500 hover:text-green-600 transition-colors duration-200 underline-offset-4 hover:underline"
                      >
                        Esqueci minha senha
                      </button>
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="signup">
                  <form onSubmit={handleSignup} className="space-y-4">


                    <div className="space-y-2">
                      <Label htmlFor="signup-company">Empresa</Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="signup-company"
                          type="text"
                          value={signupData.companyName}
                          onChange={(e) => setSignupData(prev => ({ ...prev, companyName: e.target.value }))}
                          placeholder="Nome da empresa"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-phone">Telefone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="signup-phone"
                          type="tel"
                          value={signupData.phone}
                          onChange={(e) => setSignupData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="(99) 99999-9999"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="signup-email"
                          type="email"
                          value={signupData.email}
                          onChange={(e) => setSignupData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="seu@email.com"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Senha</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="signup-password"
                          type={showSignupPassword ? "text" : "password"}
                          value={signupData.password}
                          onChange={(e) => setSignupData(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Mínimo 6 caracteres"
                          className="pl-10 pr-10"
                          required
                          minLength={6}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1 h-8 w-8 hover:bg-transparent"
                          onClick={() => setShowSignupPassword(!showSignupPassword)}
                        >
                          {showSignupPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-confirm-password">Confirmar Senha</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="signup-confirm-password"
                          type={showSignupConfirmPassword ? "text" : "password"}
                          value={signupData.confirmPassword}
                          onChange={(e) => setSignupData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          placeholder="Digite a senha novamente"
                          className="pl-10 pr-10"
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1 h-8 w-8 hover:bg-transparent"
                          onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)}
                        >
                          {showSignupConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {signupData.password !== signupData.confirmPassword && signupData.confirmPassword && (
                      <p className="text-sm text-red-500">As senhas não coincidem</p>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-11 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                      disabled={isLoading || signupData.password !== signupData.confirmPassword}
                    >
                      {isLoading ? "Cadastrando..." : "Criar Conta"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de Recuperação de Senha */}
      <Dialog open={showResetModal} onOpenChange={handleCloseResetModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resetSuccess ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Email Enviado!
                </>
              ) : (
                <>
                  <Mail className="h-5 w-5 text-green-600" />
                  Recuperar Senha
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {resetSuccess ? (
                "Enviamos um link de recuperação para o seu email. Verifique sua caixa de entrada e spam."
              ) : (
                "Digite seu email cadastrado e enviaremos um link para redefinir sua senha."
              )}
            </DialogDescription>
          </DialogHeader>

          {resetSuccess ? (
            <div className="py-6">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-sm text-gray-600 text-center">
                  Não recebeu o email? Verifique a pasta de spam ou
                  <button
                    type="button"
                    onClick={() => setResetSuccess(false)}
                    className="text-green-600 hover:text-green-700 font-medium ml-1"
                  >
                    tente novamente
                  </button>
                </p>
              </div>
              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  onClick={handleCloseResetModal}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                >
                  Entendi
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="pl-10"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseResetModal}
                  className="w-full sm:w-auto"
                  disabled={isResetting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="w-full sm:w-auto bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                  disabled={isResetting || !resetEmail}
                >
                  {isResetting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Enviar link"
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auth;
